/**
 * Procedural chunk generator for the endless run. Pure TypeScript, deterministic per
 * (seed, chunk index), validated by `chunkProblems` against the jump model in the tuning so
 * every chunk is provably passable at run speed: pits narrower than the run jump, terraces and
 * ledges lower than the apex, moving platforms whose extremes stay within reach, springs that
 * launch high enough for their ledge.
 *
 * The world is a strip: chunks of CHUNK_W px laid end to end; the floor is a 'ground' block per
 * chunk whose top may step up or down between chunks (terraces). Everything is world px; a
 * platform's (x, y) is the top-left of its collision rectangle, y = walkable top.
 */
import {
  jumpApexPx,
  jumpHeightAtDistance,
  jumpRangePx,
  launchApexPx,
  type PlayerTuning,
} from '../player/tuning';
import { LEVEL_LENGTH_PX, difficultyAt, levelAt, type Feature } from './levels';
import { Rng, hashSeed } from './rng';

export const CHUNK_W = 1000;
/** The world is taller than the 720 px view: the camera follows Georgi vertically. */
export const WORLD_H = 1080;
/** Base floor top and the highest terrace top (towers climb up to here). */
export const GROUND_MAX_Y = 620;
export const GROUND_MIN_Y = 380;
/** The first chunks of a run are flat and safe; so is the first chunk of every level. */
export const SAFE_START_CHUNKS = 2;
export const SPRING_VELOCITY = 1100;
/** Feet must clear a top by this much at the apex for a landing to be reliable. */
export const CLEARANCE_PX = 10;

/** 'ice' is a ground block with a slippery top (a third of the usual acceleration and braking). */
export type GenPlatformKind = 'ground' | 'solid' | 'one-way' | 'moving' | 'crumble' | 'ice';
export const ICE_ACCEL_SCALE = 0.35;

export interface GenMotion {
  axis: 'x' | 'y';
  /** Half of the travel: the platform oscillates position ± amplitude. */
  amplitude: number;
  periodMs: number;
  /** 0..1 phase offset. */
  phase: number;
}

export interface GenPlatform {
  id: string;
  kind: GenPlatformKind;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Rest position is (x, y); moving platforms oscillate around it. */
  motion?: GenMotion;
}

export interface GenHazard {
  id: string;
  kind: 'spikes';
  x: number;
  y: number;
  w: number;
  h: number;
}

export type PickupKind = 'bone' | 'golden' | 'feather' | 'shield' | 'magnet' | 'boots';
export const POWERUP_PICKUPS: readonly PickupKind[] = ['feather', 'shield', 'magnet', 'boots'];

export interface GenPickup {
  id: string;
  kind: PickupKind;
  x: number;
  y: number;
}

export interface GenSpring {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  velocity: number;
}

export type CreatureKind = 'beetle' | 'bat' | 'rock';

/**
 * Creatures: a beetle patrols its platform between the edges (stomp it, or it bites); a bat
 * hovers on a sine wave over a gap (stomp it from above); a rock hangs over the path and drops
 * when Georgi comes near (dodge it; it shatters on the ground).
 */
export interface GenCreature {
  id: string;
  kind: CreatureKind;
  x: number;
  /** beetle: platform top it walks on; bat: flight centre; rock: hanging height. */
  y: number;
  /** beetle: px/s; bat: sine period in ms; rock: unused. */
  speed: number;
  /** bat: vertical amplitude; rock: trigger distance; beetle: unused. */
  range: number;
}

export type ZoneKind = 'wind';

/** A wind zone pushes Georgi horizontally (px/s², signed) while he is inside it. */
export interface GenZone {
  id: string;
  kind: ZoneKind;
  x: number;
  y: number;
  w: number;
  h: number;
  force: number;
}

/** A rolling thorn ball: rolls left along the ground toward Georgi; jump over it or stomp it. */
export interface GenRoller {
  id: string;
  x: number;
  /** Ground top it rolls on. */
  y: number;
  radius: number;
  speed: number;
}

export interface GenFlag {
  id: string;
  x: number;
  y: number;
  levelIndex: number;
  name: string;
  subtitle: string;
}

export type Pattern =
  | 'flat'
  | 'pit'
  | 'ledges'
  | 'terrace'
  | 'spikes'
  | 'moving'
  | 'crumble'
  | 'spring'
  | 'gauntlet'
  | 'ice'
  | 'wind';

export interface Chunk {
  index: number;
  x0: number;
  x1: number;
  pattern: Pattern;
  difficulty: number;
  levelIndex: number;
  entryGroundY: number;
  exitGroundY: number;
  platforms: GenPlatform[];
  hazards: GenHazard[];
  pickups: GenPickup[];
  springs: GenSpring[];
  rollers: GenRoller[];
  creatures: GenCreature[];
  zones: GenZone[];
  flags: GenFlag[];
}

export const ROLLER_RADIUS = 22;
export const ROCK_TRIGGER_PX = 150;
export const WIND_FORCE = 620;

export interface ReachModel {
  runRange: number;
  apex: number;
  springApex: number;
  bodyW: number;
  maxRise: number;
  maxGap(d: number): number;
  /** Rise (px) a running jump can still land on after `gap` px of flight. */
  riseAfterGap(gap: number): number;
}

export function reachModel(t: PlayerTuning): ReachModel {
  const runRange = jumpRangePx(t, t.runSpeed);
  const apex = jumpApexPx(t);
  const bodyW = t.bodies.ground.w * t.worldScale;
  return {
    runRange,
    apex,
    springApex: launchApexPx(t, SPRING_VELOCITY),
    bodyW,
    maxRise: apex - CLEARANCE_PX,
    maxGap: (d) => lerp(110, Math.min(250, runRange * 0.85), d),
    riseAfterGap: (gap) => jumpHeightAtDistance(t, t.runSpeed, Math.max(0, gap)) - CLEARANCE_PX,
  };
}

const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

export function isLevelStartChunk(index: number): boolean {
  return index > 0 && (index * CHUNK_W) % LEVEL_LENGTH_PX === 0;
}

function patternWeights(features: readonly Feature[], d: number): [Pattern, number][] {
  const has = (f: Feature) => features.includes(f);
  const out: [Pattern, number][] = [['flat', 1.2 - d]];
  if (has('pit')) out.push(['pit', 3]);
  if (has('ledge')) out.push(['ledges', 2]);
  if (has('terrace')) out.push(['terrace', 1.6]);
  if (has('spikes')) out.push(['spikes', 2]);
  if (has('moving')) out.push(['moving', 1.6]);
  if (has('crumble')) out.push(['crumble', 1.6]);
  if (has('spring')) out.push(['spring', 1.2]);
  if (has('spikes') && has('pit') && d >= 0.55) out.push(['gauntlet', 1 + d]);
  if (has('ice')) out.push(['ice', 1.8]);
  if (has('wind')) out.push(['wind', 1.6]);
  return out;
}

interface Build {
  rng: Rng;
  index: number;
  /** Safe chunks (run start, level start) carry no hazards of any kind. */
  safe: boolean;
  x0: number;
  x1: number;
  gy: number;
  d: number;
  reach: ReachModel;
  features: readonly Feature[];
  ids: number;
  platforms: GenPlatform[];
  hazards: GenHazard[];
  pickups: GenPickup[];
  springs: GenSpring[];
  rollers: GenRoller[];
  creatures: GenCreature[];
  zones: GenZone[];
  exitGroundY: number;
}

const id = (b: Build, prefix: string) => `${prefix}-${b.x0}-${b.ids++}`;

function ground(
  b: Build,
  x: number,
  w: number,
  top: number,
  kind: 'ground' | 'ice' = 'ground',
): void {
  b.platforms.push({ id: id(b, kind), kind, x, y: top, w, h: WORLD_H - top });
}

function ledge(
  b: Build,
  x: number,
  w: number,
  top: number,
  kind: GenPlatformKind = 'one-way',
): GenPlatform {
  const p: GenPlatform = { id: id(b, kind), kind, x, y: top, w, h: 20 };
  b.platforms.push(p);
  return p;
}

function bonesArc(
  b: Build,
  cx: number,
  baseY: number,
  n: number,
  spread: number,
  lift: number,
): void {
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const x = cx + (t - 0.5) * spread;
    const y = baseY - lift * Math.sin(Math.PI * t);
    b.pickups.push({ id: id(b, 'bone'), kind: 'bone', x, y });
  }
}

function bonesLine(b: Build, x: number, y: number, n: number, step = 52): void {
  for (let i = 0; i < n; i++)
    b.pickups.push({ id: id(b, 'bone'), kind: 'bone', x: x + i * step, y });
}

function golden(b: Build, x: number, y: number): void {
  b.pickups.push({
    id: id(b, 'golden'),
    kind: b.features.includes('golden') ? 'golden' : 'bone',
    x,
    y,
  });
}

function maybePowerup(b: Build, x: number, y: number, chance: number): void {
  if (!b.rng.chance(chance)) return;
  x = clamp(x, b.x0 + 40, b.x1 - 40);
  const kind = b.rng.weighted<PickupKind>([
    ['feather', 3],
    ['magnet', 3],
    ['shield', 2],
    ['boots', 2],
  ]);
  b.pickups.push({ id: id(b, 'powerup'), kind, x, y });
}

/** A rolling thorn on a ground top at x (only outside safe chunks and where the level allows). */
function maybeRoller(b: Build, x: number, top: number, chance: number): void {
  if (b.safe || !b.features.includes('roller') || !b.rng.chance(chance)) return;
  b.rollers.push({
    id: id(b, 'roller'),
    x: clamp(x, b.x0 + 60, b.x1 - 60),
    y: top,
    radius: ROLLER_RADIUS,
    speed: Math.round(lerp(110, 210, b.d)),
  });
}

/** A beetle patrolling a platform top (outside safe chunks, where the level allows). */
function maybeBeetle(b: Build, p: GenPlatform, chance: number): void {
  if (b.safe || !b.features.includes('beetle') || p.w < 160 || !b.rng.chance(chance)) return;
  b.creatures.push({
    id: id(b, 'beetle'),
    kind: 'beetle',
    x: p.x + p.w / 2,
    y: p.y,
    speed: Math.round(lerp(70, 130, b.d)),
    range: 0,
  });
}

/** A bat hovering at a height over x (typically a gap or a ledge run). */
function maybeBat(b: Build, x: number, top: number, chance: number): void {
  if (b.safe || !b.features.includes('bat') || !b.rng.chance(chance)) return;
  b.creatures.push({
    id: id(b, 'bat'),
    kind: 'bat',
    x: clamp(x, b.x0 + 80, b.x1 - 80),
    y: top - Math.round(lerp(95, 125, b.rng.next())),
    speed: Math.round(lerp(2.6, 1.6, b.d) * 1000),
    range: Math.round(lerp(18, 34, b.d)),
  });
}

/** A rock hanging over the ground at x; it drops when Georgi comes within ROCK_TRIGGER_PX. */
function maybeRock(b: Build, x: number, top: number, chance: number): void {
  if (b.safe || !b.features.includes('rock') || !b.rng.chance(chance)) return;
  b.creatures.push({
    id: id(b, 'rock'),
    kind: 'rock',
    x: clamp(x, b.x0 + 80, b.x1 - 80),
    y: top - 280,
    speed: 0,
    range: ROCK_TRIGGER_PX,
  });
}

function buildFlat(b: Build): void {
  ground(b, b.x0, CHUNK_W, b.gy);
  // The very first chunk keeps its bones clear of the spawn so a fresh run starts at 0 points.
  const from = b.index === 0 ? 560 : b.rng.range(200, 400);
  if (b.rng.chance(0.8)) bonesLine(b, b.x0 + from, b.gy - 70, b.rng.int(3, 6));
  maybePowerup(b, b.x0 + b.rng.range(500, 800), b.gy - 110, b.index === 0 ? 0 : 0.25);
  maybeRoller(b, b.x0 + b.rng.range(650, 900), b.gy, 0.35 + 0.4 * b.d);
  if (b.rollers.length === 0) maybeBeetle(b, b.platforms[0]!, 0.25 + 0.3 * b.d);
  maybeRock(b, b.x0 + b.rng.range(420, 700), b.gy, 0.25 + 0.3 * b.d);
}

function buildPit(b: Build): void {
  const gap = Math.round(lerp(110, b.reach.maxGap(b.d), b.rng.range(0.5, 1)));
  const a = Math.round(b.rng.range(280, 460));
  ground(b, b.x0, a, b.gy);
  ground(b, b.x0 + a + gap, CHUNK_W - a - gap, b.gy);
  bonesArc(b, b.x0 + a + gap / 2, b.gy - 40, 5, gap + 120, 80);
  maybeBat(b, b.x0 + a + gap / 2, b.gy, 0.3 + 0.3 * b.d);
  if (b.features.includes('ledge') && b.rng.chance(0.4)) {
    const top = b.gy - Math.round(lerp(80, b.reach.maxRise - 6, b.d));
    ledge(b, b.x0 + a - 40, gap + 80, top);
    bonesLine(b, b.x0 + a, top - 50, 3);
  }
  maybePowerup(b, b.x0 + a + gap + 200, b.gy - 110, 0.12);
}

function buildLedges(b: Build): void {
  ground(b, b.x0, CHUNK_W, b.gy);
  const n = b.rng.int(2, 3);
  let top = b.gy - Math.round(lerp(60, 100, b.d));
  let x = b.x0 + Math.round(b.rng.range(120, 220));
  for (let i = 0; i < n; i++) {
    const w = Math.min(b.rng.int(140, 220), b.x1 - x);
    if (w < 140) break;
    ledge(b, x, w, top);
    bonesLine(b, x + 30, top - 50, Math.max(2, Math.floor((w - 40) / 52)));
    const gap = Math.round(b.rng.range(80, Math.min(160, b.reach.maxGap(b.d))));
    const nextX = x + w + gap;
    if (nextX + 140 > b.x1) break;
    const rise = Math.round(lerp(30, 80, b.d));
    top = Math.max(GROUND_MIN_Y - 180, top - rise);
    x = nextX;
  }
  if (b.features.includes('golden') && b.rng.chance(0.5)) golden(b, x - 20, top - 60);
  maybePowerup(b, b.x0 + 60, b.gy - 100, 0.1);
  const first = b.platforms[1];
  if (first) maybeBeetle(b, first, 0.3 + 0.3 * b.d);
  maybeBat(b, b.x0 + 520, b.gy - 40, 0.2 + 0.2 * b.d);
}

function buildTerrace(b: Build): void {
  const at = b.x0 + Math.round(b.rng.range(360, 560));
  const up = b.rng.chance(0.55);
  let next = up ? b.gy - Math.round(b.rng.range(40, 90)) : b.gy + Math.round(b.rng.range(40, 140));
  next = clamp(next, GROUND_MIN_Y, GROUND_MAX_Y);
  if (next === b.gy) next = b.gy === GROUND_MAX_Y ? b.gy - 60 : GROUND_MAX_Y;
  ground(b, b.x0, at - b.x0, b.gy);
  ground(b, at, b.x1 - at, next);
  b.exitGroundY = next;
  const high = Math.min(b.gy, next);
  bonesLine(b, (next < b.gy ? at : b.x0) + 60, high - 70, 4);
  maybePowerup(b, at + 300, next - 110, 0.15);
  maybeRoller(b, at + 380, next, 0.3 + 0.3 * b.d);
}

function buildSpikes(b: Build): void {
  ground(b, b.x0, CHUNK_W, b.gy);
  const w = Math.round(lerp(60, 140, b.d));
  const x = b.x0 + Math.round(b.rng.range(300, 520));
  b.hazards.push({ id: id(b, 'spikes'), kind: 'spikes', x, y: b.gy - 24, w, h: 24 });
  bonesArc(b, x + w / 2, b.gy - 50, 5, w + 160, 80);
  maybePowerup(b, b.x0 + 120, b.gy - 100, 0.12);
  maybeRoller(b, b.x0 + 880, b.gy, 0.3 + 0.3 * b.d);
  maybeRock(b, x + w / 2 + 220, b.gy, 0.25 + 0.3 * b.d);
}

function buildMoving(b: Build): void {
  const margin = Math.round(lerp(60, Math.min(150, b.reach.maxGap(b.d) - 20), b.d));
  const w = 150;
  const gap = Math.round(lerp(320, 480, b.d));
  const a = Math.round(b.rng.range(220, 320));
  ground(b, b.x0, a, b.gy);
  ground(b, b.x0 + a + gap, CHUNK_W - a - gap, b.gy);
  const travel = gap - w - 2 * margin; // total x travel of the platform
  const cx = b.x0 + a + margin + travel / 2;
  const top = b.gy - Math.round(b.rng.range(0, 30));
  const p = ledge(b, cx, w, top, 'moving');
  p.motion = {
    axis: 'x',
    amplitude: travel / 2,
    periodMs: Math.round(lerp(3200, 2200, b.d)),
    phase: b.rng.next(),
  };
  bonesArc(b, b.x0 + a + gap / 2, top - 70, 4, gap * 0.6, 40);
  maybePowerup(b, b.x0 + a + gap + 200, b.gy - 110, 0.12);
}

function buildCrumble(b: Build): void {
  const maxGap = b.reach.maxGap(b.d) - 20;
  const w = 120;
  const gap = Math.round(lerp(340, 520, b.d));
  const a = Math.round(b.rng.range(200, 300));
  ground(b, b.x0, a, b.gy);
  ground(b, b.x0 + a + gap, CHUNK_W - a - gap, b.gy);
  // Two crumbling ledges split the gap into three hops, each within reach.
  const hop = (gap - 2 * w) / 3;
  const g = Math.min(hop, maxGap);
  const top = b.gy - Math.round(lerp(10, 40, b.d));
  let x = b.x0 + a + g;
  for (let i = 0; i < 2; i++) {
    ledge(b, x, w, top, 'crumble');
    bonesLine(b, x + 20, top - 50, 2);
    x += w + g;
  }
  maybePowerup(b, b.x0 + a + gap + 220, b.gy - 110, 0.12);
}

function buildSpring(b: Build): void {
  ground(b, b.x0, CHUNK_W, b.gy);
  const sx = b.x0 + Math.round(b.rng.range(220, 320));
  b.springs.push({
    id: id(b, 'spring'),
    x: sx,
    y: b.gy - 16,
    w: 56,
    h: 16,
    velocity: SPRING_VELOCITY,
  });
  const rise = Math.round(lerp(170, b.reach.springApex - CLEARANCE_PX - 10, b.d));
  const top = b.gy - rise;
  const lx = sx + 140;
  const lw = 220;
  ledge(b, lx, lw, top);
  golden(b, lx + lw / 2, top - 60);
  bonesArc(b, sx + 28, b.gy - 80, 4, 60, rise * 0.7);
  maybePowerup(b, b.x0 + 800, b.gy - 100, 0.12);
}

function buildGauntlet(b: Build): void {
  const gap = Math.round(lerp(110, b.reach.maxGap(b.d) * 0.85, b.d));
  const spikeW = Math.round(lerp(60, 90, b.d));
  const a = 420;
  ground(b, b.x0, a, b.gy);
  ground(b, b.x0 + a + gap, CHUNK_W - a - gap, b.gy);
  b.hazards.push({
    id: id(b, 'spikes'),
    kind: 'spikes',
    x: b.x0 + 200,
    y: b.gy - 24,
    w: spikeW,
    h: 24,
  });
  b.hazards.push({
    id: id(b, 'spikes'),
    kind: 'spikes',
    x: b.x0 + a + gap + 150,
    y: b.gy - 24,
    w: spikeW,
    h: 24,
  });
  bonesArc(b, b.x0 + a + gap / 2, b.gy - 40, 5, gap + 100, 80);
  golden(b, b.x0 + a + gap + 150 + spikeW / 2, b.gy - 120);
}

/** Ice: slippery ground the whole chunk, with a pit in the middle — the run-up is the challenge. */
function buildIce(b: Build): void {
  const gap = Math.round(lerp(100, b.reach.maxGap(b.d) * 0.72, b.rng.range(0.4, 1)));
  const a = Math.round(b.rng.range(300, 480));
  ground(b, b.x0, a, b.gy, 'ice');
  ground(b, b.x0 + a + gap, CHUNK_W - a - gap, b.gy, 'ice');
  bonesArc(b, b.x0 + a + gap / 2, b.gy - 40, 5, gap + 120, 80);
  bonesLine(b, b.x0 + 80, b.gy - 70, 3);
  maybePowerup(b, b.x0 + a + gap + 260, b.gy - 110, 0.15);
  maybeBat(b, b.x0 + a + gap + 200, b.gy, 0.25 + 0.25 * b.d);
}

/** Wind: a zone pushing left or right across most of the chunk, over a modest pit. */
function buildWind(b: Build): void {
  const dir = b.rng.chance(0.5) ? -1 : 1;
  const gap = Math.round(lerp(100, b.reach.maxGap(b.d) * 0.75, b.rng.range(0.4, 1)));
  const a = Math.round(b.rng.range(320, 480));
  ground(b, b.x0, a, b.gy);
  ground(b, b.x0 + a + gap, CHUNK_W - a - gap, b.gy);
  b.zones.push({
    id: id(b, 'wind'),
    kind: 'wind',
    x: b.x0 + 100,
    y: b.gy - 320,
    w: CHUNK_W - 200,
    h: 320,
    force: dir * Math.round(lerp(WIND_FORCE * 0.7, WIND_FORCE, b.d)),
  });
  bonesArc(b, b.x0 + a + gap / 2, b.gy - 40, 5, gap + 120, 80);
  maybePowerup(b, b.x0 + 140, b.gy - 100, 0.12);
}

const BUILDERS: Record<Pattern, (b: Build) => void> = {
  flat: buildFlat,
  pit: buildPit,
  ledges: buildLedges,
  terrace: buildTerrace,
  spikes: buildSpikes,
  moving: buildMoving,
  crumble: buildCrumble,
  spring: buildSpring,
  gauntlet: buildGauntlet,
  ice: buildIce,
  wind: buildWind,
};

export function generateChunk(
  seed: number,
  index: number,
  entryGroundY: number,
  tuning: PlayerTuning,
): Chunk {
  const x0 = index * CHUNK_W;
  const x1 = x0 + CHUNK_W;
  const level = levelAt(x0);
  const d = difficultyAt(x0 + CHUNK_W / 2);
  const rng = new Rng(hashSeed(seed, index));
  const reach = reachModel(tuning);
  const gy = clamp(Math.round(entryGroundY), GROUND_MIN_Y, GROUND_MAX_Y);
  const levelStart = isLevelStartChunk(index);
  const safe = index < SAFE_START_CHUNKS || levelStart;
  const pattern: Pattern = safe ? 'flat' : rng.weighted(patternWeights(level.features, d));
  const b: Build = {
    rng,
    index,
    safe,
    x0,
    x1,
    gy,
    d,
    reach,
    features: level.features,
    ids: 0,
    platforms: [],
    hazards: [],
    pickups: [],
    springs: [],
    rollers: [],
    creatures: [],
    zones: [],
    exitGroundY: gy,
  };
  BUILDERS[pattern](b);
  const flags: GenFlag[] = levelStart
    ? [
        {
          id: `flag-${x0}`,
          x: x0 + 90,
          y: gy,
          levelIndex: level.index,
          name: level.name,
          subtitle: level.subtitle,
        },
      ]
    : [];
  return {
    index,
    x0,
    x1,
    pattern,
    difficulty: d,
    levelIndex: level.index,
    entryGroundY: gy,
    exitGroundY: b.exitGroundY,
    platforms: b.platforms,
    hazards: b.hazards,
    pickups: b.pickups,
    springs: b.springs,
    rollers: b.rollers,
    creatures: b.creatures,
    zones: b.zones,
    flags,
  };
}

// ------------------------------------------------------------------------------ validation

interface Surface {
  id: string;
  left: number;
  right: number;
  top: number;
  main: boolean;
  /** Extremes for moving platforms (rest position otherwise). */
  minLeft: number;
  maxRight: number;
}

function surfaces(chunk: Chunk): Surface[] {
  return chunk.platforms.map((p) => {
    const ax = p.motion && p.motion.axis === 'x' ? p.motion.amplitude : 0;
    return {
      id: p.id,
      left: p.x,
      right: p.x + p.w,
      top: p.y,
      main:
        p.kind === 'ground' ||
        p.kind === 'ice' ||
        p.kind === 'solid' ||
        p.kind === 'moving' ||
        p.kind === 'crumble',
      minLeft: p.x - ax,
      maxRight: p.x + p.w + ax,
    };
  });
}

/** Every reason a chunk would not be passable or well-formed (empty = valid). */
export function chunkProblems(chunk: Chunk, prev: Chunk | null, tuning: PlayerTuning): string[] {
  const problems: string[] = [];
  const reach = reachModel(tuning);
  const d = chunk.difficulty;
  const ids = new Set<string>();
  const all = [
    ...chunk.platforms,
    ...chunk.hazards,
    ...chunk.pickups,
    ...chunk.springs,
    ...chunk.rollers,
    ...chunk.creatures,
    ...chunk.zones,
    ...chunk.flags,
  ];
  for (const e of all) {
    if (ids.has(e.id)) problems.push(`${e.id}: duplicate id`);
    ids.add(e.id);
    const w = 'w' in e ? e.w : 0;
    if (e.x < chunk.x0 - 1 || e.x + w > chunk.x1 + 1) problems.push(`${e.id}: leaves the chunk`);
    if (e.y < GROUND_MIN_Y - 320 || e.y > WORLD_H) problems.push(`${e.id}: y ${e.y} out of range`);
  }
  if (chunk.exitGroundY < GROUND_MIN_Y || chunk.exitGroundY > GROUND_MAX_Y)
    problems.push(`exit ground ${chunk.exitGroundY} out of range`);
  if (prev && prev.exitGroundY !== chunk.entryGroundY)
    problems.push(`entry ground ${chunk.entryGroundY} != previous exit ${prev.exitGroundY}`);

  // Main path: every consecutive pair of main surfaces (with the previous chunk's exit) is
  // within a running jump: gap ≤ maxGap(d) and the rise reachable after that gap.
  const list = surfaces(chunk);
  const main = list.filter((s) => s.main).sort((a, b) => a.left - b.left);
  const prevExit = prev
    ? surfaces(prev)
        .filter((s) => s.main)
        .sort((a, b) => b.right - a.right)[0]
    : null;
  const path = prevExit ? [prevExit, ...main] : main;
  const maxGap = reach.maxGap(d);
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const gap = b.minLeft - a.maxRight;
    const rise = a.top - b.top;
    if (gap > maxGap + 0.5)
      problems.push(`${a.id} → ${b.id}: gap ${gap.toFixed(0)} exceeds ${maxGap.toFixed(0)}`);
    // Centre-to-centre flight of `gap`: a takeoff at the lip and a landing on the far lip's
    // edge each spare half a body width, so this is the mid-timing requirement.
    const allowedRise = gap > 0 ? reach.riseAfterGap(gap) : reach.maxRise;
    if (rise > allowedRise + 0.5)
      problems.push(
        `${a.id} → ${b.id}: rise ${rise} exceeds ${allowedRise.toFixed(0)} after gap ${gap.toFixed(0)}`,
      );
  }
  // Optional ledges: reachable from some surface below within a standing jump, or from a spring.
  const springs = chunk.springs;
  for (const s of list.filter((x) => !x.main)) {
    const fromSurface = list.some(
      (o) =>
        o !== s &&
        o.top >= s.top &&
        o.top - s.top <= reach.maxRise &&
        o.maxRight + maxGap >= s.minLeft &&
        o.minLeft - maxGap <= s.maxRight,
    );
    const fromSpring = springs.some(
      (sp) =>
        sp.y - s.top <= reach.springApex - CLEARANCE_PX &&
        sp.x + sp.w + 320 >= s.left &&
        sp.x <= s.right,
    );
    if (!fromSurface && !fromSpring) problems.push(`${s.id}: unreachable ledge`);
  }
  // Hazards and springs sit on a ground top.
  for (const h of [...chunk.hazards, ...chunk.springs]) {
    const onGround = main.some(
      (g) => g.left <= h.x && h.x + h.w <= g.right && Math.abs(g.top - (h.y + h.h)) < 0.5,
    );
    if (!onGround) problems.push(`${h.id}: not resting on ground`);
  }
  // Rollers start on a ground top, outside spikes, never in safe chunks.
  const safeChunk = chunk.index < SAFE_START_CHUNKS || isLevelStartChunk(chunk.index);
  for (const r of chunk.rollers) {
    if (safeChunk) problems.push(`${r.id}: roller in a safe chunk`);
    if (!main.some((g) => g.left <= r.x && r.x <= g.right && g.top === r.y))
      problems.push(`${r.id}: not on a ground top`);
    if (chunk.hazards.some((h) => r.x >= h.x - r.radius && r.x <= h.x + h.w + r.radius))
      problems.push(`${r.id}: overlaps spikes`);
  }
  // Creatures: beetles on a platform top, bats airborne within the view band, rocks over ground.
  for (const c of chunk.creatures) {
    if (safeChunk) problems.push(`${c.id}: creature in a safe chunk`);
    if (c.kind === 'beetle' && !list.some((p) => p.left <= c.x && c.x <= p.right && p.top === c.y))
      problems.push(`${c.id}: beetle not on a platform top`);
    if (c.kind === 'bat' && (c.y < GROUND_MIN_Y - 320 || c.y > GROUND_MAX_Y - 60))
      problems.push(`${c.id}: bat height ${c.y} out of band`);
    if (c.kind === 'rock' && !main.some((g) => g.left <= c.x && c.x <= g.right && g.top > c.y))
      problems.push(`${c.id}: rock not over ground`);
  }
  // Wind zones sit inside the chunk above the ground; gaps under wind stay well within reach.
  for (const z of chunk.zones) {
    if (z.x < chunk.x0 || z.x + z.w > chunk.x1) problems.push(`${z.id}: zone leaves the chunk`);
    if (Math.abs(z.force) > WIND_FORCE + 0.5) problems.push(`${z.id}: wind too strong`);
  }
  if (chunk.zones.length) {
    for (let i = 1; i < main.length; i++) {
      const gap = main[i]!.minLeft - main[i - 1]!.maxRight;
      if (gap > maxGap * 0.75 + 0.5)
        problems.push(`${main[i]!.id}: gap ${gap.toFixed(0)} too wide under wind`);
    }
  }
  // Pickups hover within a jump of a surface (arcs over pits are the point) and never inside spikes.
  const reachX = maxGap / 2 + 80;
  for (const p of chunk.pickups) {
    if (chunk.hazards.some((h) => p.x >= h.x && p.x <= h.x + h.w && p.y >= h.y - 8))
      problems.push(`${p.id}: inside spikes`);
    if (!list.some((s) => s.minLeft - reachX <= p.x && p.x <= s.maxRight + reachX && p.y < s.top))
      problems.push(`${p.id}: floats over nothing`);
  }
  return problems;
}
